import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightning, CaretRight } from '@phosphor-icons/react';

/**
 * Animated performance-by-tickets view (assignee leaderboard, product-agnostic).
 * Click person name OR "360°" → assignee 360°. Stats open the matching KPI view.
 */
export default function PerformanceByTicketsWidget({
  items = [],
  maxClosed = 1,
  title = 'Performance by tickets',
}) {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, [items]);

  const ranked = useMemo(
    () => [...items].sort((a, b) => (b.closed || 0) - (a.closed || 0) || (b.active || 0) - (a.active || 0)),
    [items],
  );
  const ceiling = Math.max(1, maxClosed, ...ranked.map((r) => r.closed || 0));

  const open360 = (userId, view = 'active') => {
    const sp = new URLSearchParams({
      assigned_to: userId,
      view,
    });
    navigate(`/dashboard/tickets/pipeline?${sp.toString()}`);
  };

  if (!ranked.length) {
    return (
      <div className="wd-chart-card wd-soft-card is-auto-height wd-perf-card" data-testid="performance-by-tickets">
        <div className="wd-perf-head">
          <Lightning size={18} weight="fill" className="wd-perf-icon" />
          <div>
            <p className="wd-chart-title">{title}</p>
            <p className="wd-chart-subtitle">No assignee performance data yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wd-chart-card wd-soft-card is-auto-height wd-perf-card" data-testid="performance-by-tickets">
      <div className="wd-perf-head">
        <Lightning size={18} weight="fill" className="wd-perf-icon" />
        <div>
          <p className="wd-chart-title">{title}</p>
          <p className="wd-chart-subtitle">
            Across all products · click person or 360° for assignee view
          </p>
        </div>
        <button
          type="button"
          className="wd-perf-all-pipeline"
          onClick={() => navigate('/dashboard/tickets/pipeline?mode=performance')}
        >
          Full pipeline <CaretRight size={12} weight="bold" />
        </button>
      </div>

      <div className="wd-chart-body is-scroll">
        <div className="wd-perf-list">
          {ranked.map((row, index) => {
            const closedPct = Math.round(((row.closed || 0) / ceiling) * 100);
            const activePct = Math.min(100, Math.round(((row.active || 0) / Math.max(ceiling, 1)) * 100));
            return (
              <div
                key={row.user_id}
                className={`wd-perf-row${mounted ? ' is-in' : ''}`}
                style={{ animationDelay: `${index * 55}ms`, cursor: 'default' }}
                data-testid={`perf-row-${row.user_id}`}
              >
                <span className="wd-perf-rank">{index + 1}</span>
                <div className="wd-perf-body" style={{ flex: 1, minWidth: 0 }}>
                  <div className="wd-perf-meta">
                    <button
                      type="button"
                      className="wd-perf-name-btn"
                      onClick={() => open360(row.user_id, 'active')}
                      title={`Open 360° for ${row.name}`}
                      data-testid={`perf-person-${row.user_id}`}
                    >
                      {row.name}
                    </button>
                    <span className="wd-perf-stats">
                      <button
                        type="button"
                        className="wd-perf-stat-btn"
                        onClick={() => open360(row.user_id, 'closed')}
                      >
                        <strong>{row.closed || 0}</strong> closed
                      </button>
                      <span className="wd-perf-dot">·</span>
                      <button
                        type="button"
                        className="wd-perf-stat-btn"
                        onClick={() => open360(row.user_id, 'active')}
                      >
                        {row.active || 0} active
                      </button>
                      <span className="wd-perf-dot">·</span>
                      <button
                        type="button"
                        className="wd-perf-stat-btn"
                        onClick={() => open360(row.user_id, 'total')}
                      >
                        {row.total || 0} total
                      </button>
                    </span>
                  </div>
                  <div className="wd-perf-tracks">
                    <div className="wd-perf-track wd-perf-track-closed" aria-hidden>
                      <div
                        className="wd-perf-fill wd-perf-fill-closed"
                        style={{ width: mounted ? `${closedPct}%` : '0%' }}
                      />
                    </div>
                    <div className="wd-perf-track wd-perf-track-active" aria-hidden>
                      <div
                        className="wd-perf-fill wd-perf-fill-active"
                        style={{ width: mounted ? `${activePct}%` : '0%' }}
                      />
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="wd-product-team-pipeline"
                  onClick={() => open360(row.user_id, 'active')}
                  title={`Open 360° for ${row.name}`}
                  data-testid={`perf-360-${row.user_id}`}
                  style={{ flexShrink: 0, alignSelf: 'center' }}
                >
                  360°
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <style>{`
        .wd-perf-name-btn {
          border: none;
          background: transparent;
          padding: 0;
          margin: 0;
          font: inherit;
          font-weight: 700;
          color: #111827;
          cursor: pointer;
          text-align: left;
        }
        .wd-perf-name-btn:hover {
          color: #002FA7;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .wd-perf-stat-btn {
          border: none;
          background: transparent;
          padding: 0;
          margin: 0;
          font: inherit;
          color: inherit;
          cursor: pointer;
        }
        .wd-perf-stat-btn:hover {
          color: #002FA7;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
      `}</style>
    </div>
  );
}
